import { Link } from 'react-router-dom';
import WorldScene from '@/components/world/WorldScene';
import EternityWordmark from '@/components/EternityWordmark';
import { useSettings } from '@/lib/useSettings';
import { ETERNITY } from '@/content/eternity';

/**
 * Band 1 — HERO. Film-first: the homepage opens on ETERNITY, not the studio.
 * WorldScene (or admin-set key art) behind a marquee DIN title.
 */

const HeroBand = () => {
    const settings = useSettings();
    const hasKeyArt = Boolean(settings.eternityKeyArt);

    return (
        <section className="hb-hero" aria-label="ETERNITY — feature presentation">
            {hasKeyArt ? (
                <div
                    className="hb-hero-art"
                    style={{ backgroundImage: `url(${settings.eternityKeyArt})` }}
                    aria-hidden="true"
                />
            ) : (
                <WorldScene intensity="full" />
            )}
            <div className="hb-hero-shade" aria-hidden="true" />

            <div className="hb-hero-content">
                <p className="hb-hero-kicker">
                    <span>MAXIMUM PRODUCTIONS PRESENTS</span>
                    <span className="hb-hero-kicker-dot" aria-hidden="true">·</span>
                    <span>FILM Nº 001</span>
                </p>
                <EternityWordmark as="h1" barColor="var(--color-accent-primary)" className="hb-hero-wm" />
                <div className="hb-hero-foot">
                    <p className="hb-hero-logline">{ETERNITY.hook}</p>
                    <div className="hb-hero-actions">
                        <span className="hb-hero-stamp">{ETERNITY.statusLine.toUpperCase()}</span>
                        <Link to="/eternity" className="hb-hero-cta">
                            EXPLORE THE FILM <span aria-hidden="true">→</span>
                        </Link>
                    </div>
                </div>
            </div>

            <style>{`
                .hb-hero {
                    position: relative;
                    overflow: hidden;
                    min-height: 100vh;
                    min-height: 100svh;
                    display: flex;
                    flex-direction: column;
                    justify-content: flex-end;
                    background: #04050c;
                }
                .hb-hero-art {
                    position: absolute;
                    inset: 0;
                    background-size: cover;
                    background-position: center;
                }
                .hb-hero-shade {
                    position: absolute;
                    inset: 0;
                    pointer-events: none;
                    background: linear-gradient(
                        180deg,
                        rgba(0, 0, 0, 0.55) 0%,
                        rgba(0, 0, 0, 0.1) 38%,
                        rgba(0, 0, 0, 0.62) 100%
                    );
                }
                .hb-hero-content {
                    position: relative;
                    z-index: 1;
                    padding: 0 var(--bx) clamp(2.25rem, 5vh, 4rem);
                }
                .hb-hero-kicker {
                    font-family: var(--font-mono);
                    font-size: clamp(0.6rem, 1vw, 0.78rem);
                    font-weight: 700;
                    letter-spacing: 0.26em;
                    color: rgba(255, 255, 255, 0.72);
                    margin: 0 0 clamp(1rem, 2.6vw, 3rem);
                    display: flex;
                    flex-wrap: wrap;
                    align-items: center;
                    gap: 0 0.7em;
                }
                .hb-hero-kicker-dot { color: var(--color-accent-primary); }
                .hb-hero-wm {
                    width: min(92vw, 76rem);
                    margin: 0 0 clamp(1.2rem, 3vh, 2.2rem);
                }
                .hb-hero-foot {
                    display: flex;
                    align-items: flex-end;
                    justify-content: space-between;
                    gap: 1.5rem 3rem;
                    flex-wrap: wrap;
                    border-top: 1px solid rgba(255, 255, 255, 0.22);
                    padding-top: clamp(1.1rem, 2.5vh, 1.6rem);
                }
                .hb-hero-logline {
                    font-family: var(--font-family);
                    font-size: clamp(1.05rem, 1.8vw, 1.4rem);
                    font-weight: 500;
                    line-height: 1.35;
                    color: #fff;
                    max-width: 30ch;
                    margin: 0;
                }
                .hb-hero-actions {
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    flex-wrap: wrap;
                }
                .hb-hero-stamp {
                    display: inline-flex;
                    align-items: center;
                    min-height: 44px;
                    padding: 0 1rem;
                    background: var(--color-accent-primary);
                    color: #fff;
                    font-family: var(--font-mono);
                    font-size: 0.68rem;
                    font-weight: 700;
                    letter-spacing: 0.18em;
                    white-space: nowrap;
                }
                .hb-hero-cta {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.6rem;
                    min-height: 44px;
                    padding: 0 1.4rem;
                    border: 1px solid #fff;
                    color: #fff;
                    font-family: var(--font-family);
                    font-size: 0.8rem;
                    font-weight: 700;
                    letter-spacing: 0.16em;
                    white-space: nowrap;
                    transition: background var(--transition-fast), color var(--transition-fast),
                        border-color var(--transition-fast);
                }
                .hb-hero-cta:hover {
                    background: #fff;
                    color: #000;
                }

                @media (max-width: 640px) {
                    /* On phones the WorldScene can't carry a full-height void, so
                       centre the content cluster instead of pinning it to the
                       bottom — the title/hook become the immediate focus. */
                    .hb-hero {
                        justify-content: center;
                    }
                    .hb-hero-content {
                        padding: 5rem var(--bx) 4rem;
                    }
                    /* stack the kicker into two readable lines instead of one
                       cramped full-width line */
                    .hb-hero-kicker {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 0.4rem;
                        letter-spacing: 0.22em;
                    }
                    .hb-hero-kicker-dot { display: none; }
                    .hb-hero-foot {
                        flex-direction: column;
                        align-items: flex-start;
                    }
                }
            `}</style>
        </section>
    );
};

export default HeroBand;
